import { useMemo } from "react";
import {
    Backdrop,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import CustomModalHeader from "../custom_components/CustomModalHeader";
import { formatToLongDate } from "../../utils/formatToLongDate";
import SoldItemsQR from "./SoldItemsQR";

const ItemQuantityReport = ({
    open,
    onClose,
    listInfo = [],
    data = [],
}) => {

    const reportData = useMemo(() => {

        const grouped = {};

        data.forEach((item) => {

            const itemName =
                String(
                    item.ItemName || "Unknown Item"
                ).trim();

            const category =
                String(
                    item.ItemCategory || "Uncategorized"
                ).trim();

            const quantity =
                Number(item.ItemQty) || 0;

            const price =
                Number(item.Price) || 0;

            const sfee =
                Number(item.SFee) || 0;

            const key = `${itemName}__${category}`;

            if (!grouped[key]) {

                grouped[key] = {
                    ItemName: itemName,
                    ItemCategory: category,
                    ItemQty: 0,
                    OrderCount: 0,
                    TotalAmount: 0,
                };
            }

            grouped[key].ItemQty += quantity;

            grouped[key].OrderCount += 1;

            grouped[key].TotalAmount +=
                (price * quantity) + sfee;
        });

        return Object.values(grouped);

    }, [data]);

    const totalQuantity = useMemo(() => {
        return reportData.reduce(
            (total, item) =>
                total + item.ItemQty,
            0
        );
    }, [reportData]);

    const generateSoldItemsText = (items) => {
        const header = `TOTAL SOLD ITEMS ${formatToLongDate(listInfo.DateCreated).toUpperCase()}`;

        const itemDetails = items.map(item =>
            `${item.ItemName} - ${item.OrderCount}`
        );

        return `${header}\n\n${itemDetails.join("\n")}`;
    };

    return (
        <>
            <Backdrop
                aria-hidden="false"
                open={open}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backdropFilter: "blur(1px)",
                }}
            >

                <Paper
                    sx={{
                        width: "calc(100% - 32px)",
                        maxWidth: 850,
                        maxHeight: "85vh",
                        borderRadius: 2,
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,

                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "4px",
                            background: "#2e3489"
                        }
                    }}
                >
                    <CustomModalHeader
                        title="Item List Report"
                        subtitle="Summary of items and quantities"
                        onClose={onClose}
                    />

                    <TableContainer
                        sx={{
                            mt: -2,
                            maxHeight: 600,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#fff',
                            overflow: 'auto'
                        }}
                    >
                        <Table stickyHeader size="small">

                            {/* TABLE HEADER */}
                            <TableHead>
                                <TableRow>
                                    {[
                                        '#',
                                        'Item Name',
                                        'Category',
                                        'Orders',
                                    ].map((header, index) => (
                                        <TableCell key={header}
                                            sx={{
                                                bgcolor: "#1C2536",
                                                color: "#94A3B8",
                                                fontWeight: 700,
                                                fontSize: 11,
                                                letterSpacing: "0.6px",
                                                textTransform: "uppercase",
                                                borderBottom: "none",
                                                py: 1,
                                                width: header === "#" ? 48 : header === "Actions" ? 110 : "auto",
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            {/* TABLE BODY */}
                            <TableBody>

                                {reportData.length === 0 ? (

                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            align="center"
                                            sx={{
                                                height: 220,
                                                borderBottom:
                                                    "none",
                                            }}
                                        >

                                            <Stack
                                                alignItems="center"
                                                justifyContent="center"
                                            >

                                                <Inventory2OutlinedIcon
                                                    sx={{
                                                        fontSize: 34,
                                                        color:
                                                            "#d1d5db",
                                                        mb: 1,
                                                    }}
                                                />

                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        color:
                                                            "#6b7280",
                                                    }}
                                                >
                                                    No item data
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        mt: 0.3,
                                                        fontSize: 11,
                                                        color:
                                                            "#9ca3af",
                                                    }}
                                                >
                                                    There are no items
                                                    to display.
                                                </Typography>

                                            </Stack>

                                        </TableCell>
                                    </TableRow>

                                ) : (

                                    reportData.map(
                                        (item, index) => (

                                            <TableRow key={`${item.ItemName}-${item.ItemCategory}`}  >

                                                {/* NUMBER */}
                                                <TableCell
                                                    sx={{
                                                        fontSize: 12,
                                                        color: "#9ca3af",
                                                        width: 40,
                                                    }}
                                                >
                                                    {index + 1}
                                                </TableCell>

                                                {/* ITEM NAME */}
                                                <TableCell sx={{ maxWidth: 300, }} >
                                                    <Typography
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 600,
                                                            color: "#111827",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {item.ItemName}
                                                    </Typography>
                                                </TableCell>

                                                {/* CATEGORY */}
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 600,
                                                            color: "#111827",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {item.ItemCategory}
                                                    </Typography>
                                                </TableCell>

                                                {/* ORDERS */}
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 600,
                                                            color: "#111827",
                                                        }}
                                                    >
                                                        {item.ItemQty.toLocaleString()}
                                                    </Typography>
                                                </TableCell>

                                            </TableRow>

                                        )
                                    )
                                )}

                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                        sx={{
                            px: 2,
                            py: 1.2,
                        }}
                    >

                        {/* TOTALS */}
                        <Stack
                            direction="row"
                            spacing={3}
                            alignItems="center"
                        >
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: "#9ca3af",
                                }}
                            >
                                Total Items:{" "}
                                <strong style={{ color: "#374151" }}>
                                    {reportData.length.toLocaleString()}
                                </strong>
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: "#9ca3af",
                                }}
                            >
                                Total Orders:{" "}
                                <strong style={{ color: "#374151" }}>
                                    {totalQuantity.toLocaleString()}
                                </strong>
                            </Typography>
                        </Stack>

                        {/* SCAN COPY QR POPUP */}
                        <SoldItemsQR generatedText={generateSoldItemsText(reportData)} />

                    </Stack>

                </Paper>

            </Backdrop>
        </>
    );
};

export default ItemQuantityReport;